import { DateTime } from 'luxon';

import { YandexTaskInfo, YLog } from '@/types/yandex';
import { parseDuration } from '@/utils/parseDuration';

export const DIVERSION_THRESHOLD_PERCENT = 30;
const Y_AXIS_TICK_STEP = 30;

export type VarianceCategory = 'onTimeOrFaster' | 'within30' | 'over30';

export type EstimationVariancePoint = {
  taskKey: string;
  summary: string;
  statusKey: string;
  statusDisplay: string;
  lastPeriodWorklogDate: string;
  spentMinutes: number;
  estimateMinutes: number;
  deltaPercent: number;
  category: VarianceCategory;
};

export type ChartEstimationPoint = EstimationVariancePoint & {
  latestWorklogTimestamp: number;
  displayTimestamp: number;
};

export function uniqueTaskKeysFromLogs(logs: Array<{ taskKey?: string }>): string[] {
  const keys = new Set<string>();

  logs.forEach((log) => {
    if (log.taskKey) {
      keys.add(log.taskKey);
    }
  });

  return [...keys];
}

export function isAssignedToUser(
  task: Pick<YandexTaskInfo, 'assignee'>,
  selectedTrackerUserId: string,
): boolean {
  return String(task.assignee?.id) === String(selectedTrackerUserId);
}

export function resolveEstimateMinutes(
  task: Pick<YandexTaskInfo, 'estimation' | 'originalEstimation'>,
): number {
  const estimate = task.estimation || task.originalEstimation;

  if (!estimate) {
    return 0;
  }

  return parseDuration(estimate);
}

export function filterWorklogsByUser(worklogs: YLog[], selectedTrackerUserId: string): YLog[] {
  return worklogs.filter(
    (worklog) => String(worklog.createdBy?.id) === String(selectedTrackerUserId),
  );
}

export function sumWorklogMinutes(worklogs: YLog[]): number {
  return worklogs.reduce((sum, worklog) => sum + parseDuration(worklog.duration), 0);
}

export function latestWorklog(worklogs: YLog[]): YLog | null {
  if (worklogs.length === 0) {
    return null;
  }

  return worklogs.reduce((latest, worklog) => {
    const latestMs = DateTime.fromISO(latest.start).toMillis();
    const currentMs = DateTime.fromISO(worklog.start).toMillis();

    return currentMs >= latestMs ? worklog : latest;
  });
}

export function isDateInPeriod(
  isoDate: string,
  periodStart: DateTime,
  periodEnd: DateTime,
): boolean {
  const date = DateTime.fromISO(isoDate);

  if (!date.isValid) {
    return false;
  }

  return date >= periodStart.startOf('day') && date <= periodEnd.endOf('day');
}

export function worklogsInPeriod(
  worklogs: YLog[],
  periodStart: DateTime,
  periodEnd: DateTime,
): YLog[] {
  return worklogs.filter((worklog) => isDateInPeriod(worklog.start, periodStart, periodEnd));
}

export function deltaPercent(spentMinutes: number, estimateMinutes: number): number {
  return ((spentMinutes - estimateMinutes) / estimateMinutes) * 100;
}

export function categorizeDelta(percent: number): VarianceCategory {
  if (percent <= 0) {
    return 'onTimeOrFaster';
  }

  if (percent <= DIVERSION_THRESHOLD_PERCENT) {
    return 'within30';
  }

  return 'over30';
}

export function countCategories(
  points: Array<{ deltaPercent: number }>,
): Record<VarianceCategory, number> {
  return points.reduce(
    (counts, point) => {
      counts[categorizeDelta(point.deltaPercent)] += 1;

      return counts;
    },
    { onTimeOrFaster: 0, within30: 0, over30: 0 } as Record<VarianceCategory, number>,
  );
}

export function categoryPercent(categoryCount: number, totalPlottedTasks: number): number {
  if (totalPlottedTasks === 0) {
    return 0;
  }

  return (categoryCount / totalPlottedTasks) * 100;
}

export function spreadSameDayPoints(points: EstimationVariancePoint[]): ChartEstimationPoint[] {
  const grouped = new Map<string, EstimationVariancePoint[]>();

  points.forEach((point) => {
    const dayKey = DateTime.fromISO(point.lastPeriodWorklogDate).startOf('day').toISODate();

    if (!dayKey) {
      return;
    }

    const group = grouped.get(dayKey) ?? [];
    group.push(point);
    grouped.set(dayKey, group);
  });

  const result: ChartEstimationPoint[] = [];

  grouped.forEach((group) => {
    const sorted = [...group].sort((a, b) => a.taskKey.localeCompare(b.taskKey));
    const dayStart = DateTime.fromISO(sorted[0].lastPeriodWorklogDate).startOf('day');
    const dayMs = 24 * 60 * 60 * 1000;

    sorted.forEach((point, index) => {
      const latestWorklogTimestamp = DateTime.fromISO(point.lastPeriodWorklogDate).toMillis();
      const displayTimestamp =
        sorted.length === 1
          ? latestWorklogTimestamp
          : dayStart.toMillis() + ((index + 0.5) / sorted.length) * dayMs;

      result.push({
        ...point,
        latestWorklogTimestamp,
        displayTimestamp,
      });
    });
  });

  return result.sort((a, b) => a.displayTimestamp - b.displayTimestamp);
}

export function paddedPeriodDomain(periodStart: DateTime, periodEnd: DateTime): [number, number] {
  return [
    periodStart.startOf('day').minus({ days: 1 }).toMillis(),
    periodEnd.startOf('day').plus({ days: 1 }).toMillis(),
  ];
}

export function yAxisDomain(values: number[]): [number, number] {
  const minValue = values.length > 0 ? Math.min(0, ...values) : 0;
  const maxValue =
    values.length > 0
      ? Math.max(DIVERSION_THRESHOLD_PERCENT, ...values)
      : DIVERSION_THRESHOLD_PERCENT;

  return [
    Math.min(0, Math.floor(minValue / Y_AXIS_TICK_STEP) * Y_AXIS_TICK_STEP),
    Math.max(
      DIVERSION_THRESHOLD_PERCENT,
      Math.ceil(maxValue / Y_AXIS_TICK_STEP) * Y_AXIS_TICK_STEP,
    ),
  ];
}

export function yAxisTicks(domain: [number, number]): number[] {
  const [min, max] = domain;
  const ticks = new Set<number>([0, DIVERSION_THRESHOLD_PERCENT]);

  for (
    let tick = Math.ceil(min / Y_AXIS_TICK_STEP) * Y_AXIS_TICK_STEP;
    tick <= max;
    tick += Y_AXIS_TICK_STEP
  ) {
    ticks.add(tick);
  }

  return [...ticks].sort((a, b) => a - b);
}

export function buildVariancePoint(params: {
  task: YandexTaskInfo;
  spentMinutes: number;
  estimateMinutes: number;
  lastPeriodWorklogDate: string;
}): EstimationVariancePoint {
  const percent = deltaPercent(params.spentMinutes, params.estimateMinutes);

  return {
    taskKey: params.task.key,
    summary: params.task.summary,
    statusKey: params.task.status?.key || 'unknown',
    statusDisplay: params.task.status?.display || 'Без статуса',
    lastPeriodWorklogDate: params.lastPeriodWorklogDate,
    spentMinutes: params.spentMinutes,
    estimateMinutes: params.estimateMinutes,
    deltaPercent: percent,
    category: categorizeDelta(percent),
  };
}
